import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import Toolbar from './Toolbar';
import AuthModule from './AuthModule';
import Header from './Header';
import OnboardingPopup from './OnboardingPopup';
import CookieConsent from './CookieConsent';
import '../styles/StoryReader.css';

// Story mapping
const STORIES = {
  'barfi-bazaar': {
    title: 'The Night Market ofBarfi Bazaar',
    file: '/story2-formatted.md'
  },
  'fitness-center': {
    title: 'The 24/7 Fitness Center',
    file: '/story.md'
  },
  'jadhav-mahal': {
    title: 'The Painters of Jadhav Mahal',
    file: '/story3.md'
  },
  'strange-wedding': {
    title: 'The Strange Wedding of Dhar',
    file: '/story4.md'
  },
  'bhura-ettan': {
    title: 'The Stange Life of Bhura Ettan',
    file: '/story5.md'
  },
  'dragged-to-the-depths': {
    title: 'Dragged to the Depths',
    file: '/story6.md'
  },
  'meeting-her-parents': {
    title: 'Meeting Her Parents in Sagar ( M.P )',
    file: '/story7.md'
  },
  'family-vacation-in-darjeeling': {
    title: 'A Family Vacation In Darjeeling',
    file: '/story8.md'
  },
  '3idiots-horrorified': {
    title: '3 Idiots ( Horrorified )',
    file: '/story9.md'
  },
  'the-office': {
    title: 'A Nightmare at Dubey Mittal Paper Company',
    file: '/story10.md'
  },
  'the-eyes-that-followed-him-everywhere': {
    title: 'The Eyes That Followed Him Everywhere',
    file: '/story11.md'
  },
  'the-lady-on-the-swing': {
    title: 'The Lady on the Swing',
    file: '/story12.md'
  },
  'revenge-of-the-bloodlines': {
    title: 'Revenge of the Bloodlines',
    file: '/story13.md'
  },
  'an-age-old-warning': {
    title: 'An Age Old Warning',
    file: '/story14.md'
  },
  'the-cost-of-a-late-night-maggi': {
    title: 'The Cost of a Late-Night Maggi',
    file: '/story15.md'
  },
  'k3g': {
    title: 'Kabhi Khushi Kabhi Gham ( Horrorified )',
    file: '/story16.md'
  },
  'default': {
    title: 'Barfi Bazaar',
    file: '/story2-formatted.md'
  }
};

function StoryReader() {
  const { storyId } = useParams();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  
  // Determine which story to load
  const currentStory = React.useMemo(() => {
    // If we're on /barfi-bazaar route, use that story
    if (location.pathname === '/barfi-bazaar') {
      return STORIES['barfi-bazaar'];
    }
    // Otherwise use the storyId from the URL or default
    return STORIES[storyId] || STORIES.default;
  }, [storyId, location.pathname]);
  
  // State variables
  const [story, setStory] = useState({ title: 'Loading...', content: '' });
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [fontSize, setFontSize] = useState(18);
  const [lineHeight, setLineHeight] = useState(30);
  const [rainActive, setRainActive] = useState(false);
  const [windActive, setWindActive] = useState(false);
  const [bgmActive, setBgmActive] = useState(false);
  const [isDistractedMode, setIsDistractedMode] = useState(false);
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const [remainingElements, setRemainingElements] = useState([]);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [imagesHidden, setImagesHidden] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [isThreadMode, setIsThreadMode] = useState(false);
  
  // Refs
  const authModuleRef = useRef(null);
  const mainContentRef = useRef(null);
  const lastScrollTop = useRef(0);
  const scrollThreshold = useRef(50); // Reduced threshold for mobile responsiveness
  
  // Audio refs
  const rainSoundRef = useRef(null);
  const windSoundRef = useRef(null);
  const bgmSoundRef = useRef(null);
  
  // Handle scroll to hide/show header
  const handleScroll = () => {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    
    console.log('Scroll:', currentScrollTop, 'HeaderVisible:', headerVisible);
    
    // Show header at very top
    if (currentScrollTop <= 10) {
      setHeaderVisible(true);
      lastScrollTop.current = currentScrollTop;
      return;
    }
    
    // Show header at very bottom
    if (currentScrollTop + windowHeight >= documentHeight - 10) {
      setHeaderVisible(true);
      lastScrollTop.current = currentScrollTop;
      return;
    }
    
    // Only act if scroll distance exceeds threshold
    const scrollDifference = Math.abs(currentScrollTop - lastScrollTop.current);
    if (scrollDifference < scrollThreshold.current) {
      return;
    }
    
    // Hide header when scrolling down, show when scrolling up
    if (currentScrollTop > lastScrollTop.current) {
      // Scrolling down - hide header
      console.log('Hiding header');
      setHeaderVisible(false);
    } else {
      // Scrolling up - show header
      console.log('Showing header');
      setHeaderVisible(true);
    }
    
    lastScrollTop.current = currentScrollTop;
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      // Clear cumulative page tracking when user logs in
      if (currentUser) {
        localStorage.removeItem('totalPagesRead');
        localStorage.removeItem('viewedStories');
      }
    });

    return () => unsubscribe();
  }, []);
  
  // Load saved page from localStorage on initial load
  useEffect(() => {
    if (!authLoading) {
      if (user) {
        // If user is logged in, try to get saved page
        const savedPage = localStorage.getItem('currentPage');
        if (savedPage !== null && pages.length > 0) {
          const pageNum = parseInt(savedPage, 10);
          if (!isNaN(pageNum) && pageNum >= 0 && pageNum < pages.length) {
            setCurrentPage(pageNum);
          }
        }
      } else {
        // If user is logged out, always start from page 1
        setCurrentPage(0);
      }
    }
  }, [authLoading, user, pages.length]);

  // Track cumulative pages read across all stories for unauthenticated users
  const getTotalPagesRead = () => {
    if (user) return 0; // No limit for authenticated users
    const totalPages = localStorage.getItem('totalPagesRead');
    return totalPages ? parseInt(totalPages, 10) : 0;
  };

  const incrementTotalPagesRead = () => {
    if (!user) {
      const currentTotal = getTotalPagesRead();
      localStorage.setItem('totalPagesRead', (currentTotal + 1).toString());
    }
  };

  const canReadMorePages = () => {
    if (user) return true; // Authenticated users can read unlimited pages
    return getTotalPagesRead() < 10;
  };

  // Save current page to localStorage when it changes
  useEffect(() => {
    if (user) {
      // Save page number even if it's 0 (first page)
      localStorage.setItem('currentPage', currentPage.toString());
    }
  }, [currentPage, user]);

  // Track page views for cumulative counting (except when user logs out and resets to page 0)
  useEffect(() => {
    // Only track if we have pages loaded and this isn't the initial load or logout reset
    if (pages.length > 0 && !authLoading) {
      // For the first page of any story, count it as a page view for unauthenticated users
      if (currentPage === 0 && !user) {
        // Check if we haven't already counted this story's first page
        const viewedStories = JSON.parse(localStorage.getItem('viewedStories') || '[]');
        const storyKey = currentStory.file;
        if (!viewedStories.includes(storyKey)) {
          incrementTotalPagesRead();
          viewedStories.push(storyKey);
          localStorage.setItem('viewedStories', JSON.stringify(viewedStories));
        }
      }
    }
  }, [currentPage, pages.length, authLoading, user, currentStory.file]);
  
  // Get user's first name
  const getUserFirstName = () => {
    if (!user) return null;
    
    // Try to get the display name first
    if (user.displayName) {
      return user.displayName.split(' ')[0];
    }
    
    // Fall back to email
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    return 'Reader';
  };
  
  // Handle user logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Clear saved page and reset to first page when logging out
      localStorage.removeItem('currentPage');
      setCurrentPage(0);
      
      // Scroll to auth module after a short delay to ensure it's rendered
      setTimeout(() => {
        scrollToAuthModule();
      }, 100);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Add this helper function after other utility functions in the component
  const convertToImgixUrl = (localPath) => {
    // Remove leading slash if present
    const cleanPath = localPath.startsWith('/') ? localPath.substring(1) : localPath;
    
    // Add https:// protocol to the Imgix domain
    const imgixDomain = 'https://sunnysideupstorys.imgix.net';
    
    // Add Imgix parameters for optimization
    // auto=compress,format: automatically optimize format and compression
    // q=80: 80% quality, good balance between quality and file size
    // w parameters will be added in srcset
    const imgixParams = '?auto=compress,format&q=80';
    
    // Log the complete URL for debugging
    const completeUrl = `${imgixDomain}/${cleanPath}${imgixParams}`;
    console.log('Image URL:', completeUrl);
    
    return completeUrl;
  };
  
  // Custom renderer for markdown images
  useEffect(() => {
    const renderer = new marked.Renderer();
    renderer.image = function(href, title, text) {
      // Convert local image path to Imgix URL
      const imgixUrl = convertToImgixUrl(href);
      
      // Create srcset for responsive images using Imgix
      const srcset = `
        ${imgixUrl}&w=480 480w,
        ${imgixUrl}&w=800 800w,
        ${imgixUrl}&w=1200 1200w
      `;
      
      return `<div class="image-wrapper">
                <img 
                  src="${imgixUrl}" 
                  srcset="${srcset}"
                  sizes="(max-width: 600px) 480px, (max-width: 1200px) 800px, 1200px"
                  alt="${text}" 
                  class="markdown-image" 
                  title="${title || ''}" 
                  loading="lazy"
                  draggable="false" 
                  oncontextmenu="return false;"
                >
                <div class="image-protection-overlay"></div>
                ${title ? `<div class="image-caption">${title}</div>` : ''}
              </div>`;
    };

    marked.setOptions({ 
      renderer,
      gfm: true // Enable GitHub Flavored Markdown
    });
  }, []);
  
  // Preprocess markdown to convert lines ending with double spaces into paragraph breaks
  const preprocessMarkdown = (markdown) => {
    // Convert lines ending with two or more spaces into double newlines (paragraph breaks)
    return markdown.replace(/  +\n/g, '\n\n');
  };
  
  // Prevent image context menu and drag
  useEffect(() => {
    const preventImageSaving = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };
    
    document.addEventListener('contextmenu', preventImageSaving);
    document.addEventListener('dragstart', preventImageSaving);
    
    return () => {
      document.removeEventListener('contextmenu', preventImageSaving);
      document.removeEventListener('dragstart', preventImageSaving);
    };
  }, []);
  
  // Load story
  useEffect(() => {
    fetch(currentStory.file)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load story (${response.status} ${response.statusText})`);
        }
        return response.text();
      })
      .then(markdown => {
        // Extract title from first line if it's a heading
        const lines = markdown.split('\n');
        let storyTitle = currentStory.title;
        let storyContent = markdown;
        
        if (lines[0].startsWith('# ')) {
          storyTitle = lines[0].substring(2);
          // Remove the title from the content
          storyContent = lines.slice(1).join('\n');
        }
        
        setStory({ title: storyTitle, content: storyContent });
        
        // Split content into pages
        const splitPages = splitIntoPages(storyContent);
        setPages(splitPages);
      })
      .catch(error => {
        console.error('Error loading story:', error);
        // Fallback content
        const fallbackContent = `
## Page 1

Harshal and Vicky, both 28-year-old childhood friends from Ahmedabad, had finally taken the plunge into entrepreneurship. 

They opened "**The Hound**," a lavish three-story gym located near *SG Highway*, an upscale area in *Ahmedabad*, aiming to attract the younger demographic.`;
        
        setStory({ title: currentStory.title, content: fallbackContent });
        setPages([fallbackContent]);
      });
  }, [currentStory.file, currentStory.title]);
  
  // Split content into pages
  const splitIntoPages = (markdown) => {
    // First try splitting by ## headings
    const pageBreakPattern = /(?=^## )/gm;
    let pages = markdown.split(pageBreakPattern).filter(page => page.trim() !== '');
    
    // If we have multiple pages from headers, return them
    if (pages.length > 1) {
      return pages;
    }
    
    // If no ## headings or only one section, create chunks by paragraphs
    const paragraphs = markdown.split('\n\n');
    pages = [];
    let currentPage = '';
    
    for (const paragraph of paragraphs) {
      if (currentPage.length + paragraph.length > 2000) {
        pages.push(currentPage);
        currentPage = paragraph;
      } else {
        currentPage += (currentPage ? '\n\n' : '') + paragraph;
      }
    }
    
    if (currentPage) {
      pages.push(currentPage);
    }
    
    // Ensure we have at least one page
    if (pages.length === 0) {
      pages = [markdown];
    }
    
    return pages;
  };
  
  // Process images in markdown
  const extractImagesAndRenderMarkdown = (markdown) => {
    // First preprocess the markdown to convert lines ending with double spaces into paragraph breaks
    const preprocessedMarkdown = preprocessMarkdown(markdown);
    
    // Store image references we find in the markdown
    const imageReferences = [];
    
    // Extract image patterns: ![alt](src "title")
    const imagePattern = /!\[(.*?)\]\((.*?)(\s+"(.*?)")?\)/g;
    let match;
    let processedMarkdown = preprocessedMarkdown;
    
    // Find all image references and replace with placeholders
    while ((match = imagePattern.exec(preprocessedMarkdown)) !== null) {
      const fullMatch = match[0];
      const alt = match[1] || 'Image';
      const src = match[2] || '';
      const title = match[4] || '';
      
      // Convert relative image paths to absolute
      const absoluteSrc = src.startsWith('/') ? src : `/${src}`;
      
      // Create Imgix URL for the image
      const imgixUrl = convertToImgixUrl(absoluteSrc);
      
      // Create srcset for responsive images using Imgix
      const srcset = `
        ${imgixUrl}&w=480 480w,
        ${imgixUrl}&w=800 800w,
        ${imgixUrl}&w=1200 1200w
      `;
      
      // Add image data to our array
      imageReferences.push({ 
        alt, 
        src: imgixUrl,
        srcset: srcset,
        title 
      });
      
      // Replace image markdown with a special placeholder
      const placeholder = `<div class="md-image-placeholder" data-index="${imageReferences.length - 1}"></div>`;
      processedMarkdown = processedMarkdown.replace(fullMatch, placeholder);
    }
    
    // Render the markdown normally (without images)
    let renderedHtml = marked.parse(processedMarkdown);
    
    // Now replace the placeholders with proper image HTML
    imageReferences.forEach((img, index) => {
      const imageHtml = `
        <div class="image-wrapper">
          <img 
            src="${img.src}" 
            srcset="${img.srcset}"
            sizes="(max-width: 600px) 480px, (max-width: 1200px) 800px, 1200px"
            alt="${img.alt}" 
            class="markdown-image" 
            title="${img.title}"
            loading="lazy"
            draggable="false"
            oncontextmenu="return false;"
          >
          <div class="image-protection-overlay"></div>
          ${img.title ? `<div class="image-caption">${img.title}</div>` : ''}
        </div>
      `;
      renderedHtml = renderedHtml.replace(
        `<div class="md-image-placeholder" data-index="${index}"></div>`, 
        imageHtml
      );
    });
    
    return renderedHtml;
  };
  
  // Add drop cap to first letter of first word
  const applyDropCapToFirstWord = (html) => {
    // Add drop cap to first letter of first word in first paragraph
    return html.replace(/<p>(\w)(\w*)/, '<p><span class="drop-cap">$1</span>$2');
  };
  
  // Navigate to previous page
  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Navigate to next page
  const goToNextPage = () => {
    if (currentPage < pages.length - 1) {
      // Check if user can read more pages before incrementing
      if (user || getTotalPagesRead() < 10) {
        setCurrentPage(currentPage + 1);
        incrementTotalPagesRead();
        window.scrollTo(0, 0);
      }
    }
  };
  
  // Jump to first page
  const jumpToStart = () => {
    setCurrentPage(0);
    window.scrollTo(0, 0);
  };
  
  // Jump to last page
  const jumpToEnd = () => {
    if (pages.length > 0) {
      if (user) {
        // Authenticated users can jump to end freely
        setCurrentPage(pages.length - 1);
        window.scrollTo(0, 0);
      } else {
        // For unauthenticated users, calculate how many pages they can jump
        const remainingPages = Math.max(0, 10 - getTotalPagesRead());
        const maxTargetPage = currentPage + remainingPages;
        const targetPage = Math.min(pages.length - 1, maxTargetPage);
        
        if (targetPage > currentPage) {
          const pagesJumped = targetPage - currentPage;
          setCurrentPage(targetPage);
          // Increment total pages read by the number of pages jumped
          for (let i = 0; i < pagesJumped; i++) {
            incrementTotalPagesRead();
          }
          window.scrollTo(0, 0);
        }
      }
    }
  };
  
  // Handle increment font size
  const increaseFontSize = () => {
    if (fontSize < 28) {
      const newFontSize = fontSize + 2;
      const newLineHeight = Math.round(newFontSize * 1.65);
      setFontSize(newFontSize);
      setLineHeight(newLineHeight);
      
      // Apply directly to main content and paragraph elements
      const mainContent = document.getElementById('story-content');
      if (mainContent) {
        mainContent.style.fontSize = `${newFontSize}px`;
        mainContent.style.lineHeight = `${newLineHeight}px`;
        
        // Also apply to all paragraphs within main content
        const paragraphs = mainContent.querySelectorAll('p');
        paragraphs.forEach(p => {
          p.style.fontSize = `${newFontSize}px`;
          p.style.lineHeight = `${newLineHeight}px`;
        });
        
        // Apply to thread mode text specifically
        const threadTexts = mainContent.querySelectorAll('.thread-text, .thread-text p');
        threadTexts.forEach(p => {
          p.style.fontSize = `${newFontSize}px`;
          p.style.lineHeight = `${newLineHeight}px`;
        });
      }
    }
  };
  
  // Handle decrement font size
  const decreaseFontSize = () => {
    if (fontSize > 14) {
      const newFontSize = fontSize - 2;
      const newLineHeight = Math.round(newFontSize * 1.65);
      setFontSize(newFontSize);
      setLineHeight(newLineHeight);
      
      // Apply directly to main content and paragraph elements
      const mainContent = document.getElementById('story-content');
      if (mainContent) {
        mainContent.style.fontSize = `${newFontSize}px`;
        mainContent.style.lineHeight = `${newLineHeight}px`;
        
        // Also apply to all paragraphs within main content
        const paragraphs = mainContent.querySelectorAll('p');
        paragraphs.forEach(p => {
          p.style.fontSize = `${newFontSize}px`;
          p.style.lineHeight = `${newLineHeight}px`;
        });
        
        // Apply to thread mode text specifically
        const threadTexts = mainContent.querySelectorAll('.thread-text, .thread-text p');
        threadTexts.forEach(p => {
          p.style.fontSize = `${newFontSize}px`;
          p.style.lineHeight = `${newLineHeight}px`;
        });
      }
    }
  };
  
  // Toggle toolbar and header visibility
  const toggleToolbar = () => {
    const newToolbarVisible = !toolbarVisible;
    setToolbarVisible(newToolbarVisible);
    setHeaderVisible(newToolbarVisible);
  };
  
  // Handle rain toggle
  const toggleRain = () => {
    const newState = !rainActive;
    setRainActive(newState);
    
    if (newState) {
      rainSoundRef.current.volume = 0.7;
      rainSoundRef.current.play().catch(e => {
        console.error('Error playing rain:', e);
        setTimeout(() => rainSoundRef.current.play().catch(() => {}), 100);
      });
    } else {
      rainSoundRef.current.pause();
      rainSoundRef.current.currentTime = 0;
    }
  };
  
  // Handle wind toggle
  const toggleWind = () => {
    const newState = !windActive;
    setWindActive(newState);
    
    if (newState) {
      windSoundRef.current.volume = 0.7;
      windSoundRef.current.play().catch(e => {
        console.error('Error playing wind:', e);
        setTimeout(() => windSoundRef.current.play().catch(() => {}), 100);
      });
    } else {
      windSoundRef.current.pause();
      windSoundRef.current.currentTime = 0;
    }
  };
  
  // Handle background music toggle
  const toggleBgm = () => {
    const newState = !bgmActive;
    setBgmActive(newState);
    
    if (newState) {
      bgmSoundRef.current.volume = 0.7;
      bgmSoundRef.current.play().catch(e => {
        console.error('Error playing music:', e);
        setTimeout(() => bgmSoundRef.current.play().catch(() => {}), 100);
      });
    } else {
      bgmSoundRef.current.pause();
      bgmSoundRef.current.currentTime = 0;
    }
  };
  
  // Handle distracted mode toggle
  const toggleDistractedMode = () => {
    setIsDistractedMode(!isDistractedMode);
  };
  
  // Handle audio volume changes
  const handleRainVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    rainSoundRef.current.volume = vol;
  };
  
  const handleWindVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    windSoundRef.current.volume = vol;
  };
  
  const handleBgmVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    bgmSoundRef.current.volume = vol;
  };
  
  // Distracted mode functionality
  const revealNextElement = (event) => {
    // Don't trigger on certain elements
    if (event.target.closest('.pagination') || 
        event.target.closest('.toolbar') || 
        event.target.closest('.toolbar-toggle')) {
      return;
    }
    
    if (remainingElements.length === 0) {
      // No more elements
      return;
    }
    
    // Add next element
    setRemainingElements(remainingElements.slice(1));
  };
  
  // Prepare content based on distracted mode
  useEffect(() => {
    if (isDistractedMode && pages[currentPage]) {
      if (isThreadMode) {
        // In thread mode, show threads one by one
        const threads = groupIntoThreads(pages[currentPage]);
        if (threads.length > 1) {
          setRemainingElements(threads.slice(1));
        } else {
          setRemainingElements([]);
        }
      } else {
        // Normal mode - show paragraphs one by one
        const contentElements = pages[currentPage].split('\n\n');
        if (contentElements.length > 1) {
          setRemainingElements(contentElements.slice(1));
        } else {
          setRemainingElements([]);
        }
      }
    } else {
      setRemainingElements([]);
    }
  }, [isDistractedMode, currentPage, pages, isThreadMode]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentPage > 0) {
        goToPrevPage();
      } else if (e.key === 'ArrowRight' && currentPage < pages.length - 1) {
        goToNextPage();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, pages.length]);
  
  // Check if user should see restricted content
  const shouldShowRestrictedContent = () => {
    return user !== null;
  };
  
  // Prepare HTML content for current page
  const getPageContent = () => {
    if (!pages[currentPage]) return '';

    try {
      // Check if user has exceeded the cumulative 10-page limit and is not authenticated
      if (!user && getTotalPagesRead() >= 10) {
        return `<div class="restricted-content"><p>You have reached your free reading limit of 10 pages. Please sign in to continue reading stories.</p></div>`;
      }
      
      // Thread mode rendering
      if (isThreadMode) {
        const threads = groupIntoThreads(pages[currentPage]);
        
        if (isDistractedMode) {
          // In distracted mode, show threads one by one
          const revealedThreads = threads.length - remainingElements.length;
          const visibleThreads = threads.slice(0, Math.max(1, revealedThreads));
          
          let content = visibleThreads.map((thread, index) => {
            const html = extractImagesAndRenderMarkdown(thread);
            const showAvatar = index > 0; // Only show avatar for threads after the first one
            const shouldApplyDropCap = currentPage === 0 && index === 0;
            const finalHtml = shouldApplyDropCap ? applyDropCapToFirstWord(html) : html;
            return `<div class="thread-post" data-thread-index="${index}">
              ${showAvatar ? `<div class="thread-avatar">
                <img src="/assets/Logo.PNG" alt="Story Avatar" class="thread-avatar-img">
              </div>` : ''}
              <div class="thread-content">
                ${showAvatar ? `<div class="thread-header">
                  <span class="thread-handle">@sunnysideup_storys</span>
                  <span class="thread-count">${index + 1}/${threads.length}</span>
                </div>` : ''}
                <div class="thread-text">${finalHtml}</div>
              </div>
            </div>`;
          }).join('');
          
          if (remainingElements.length > 0) {
            content += '<div class="tap-to-continue">Tap to Continue</div>';
          }
          
          return content;
        } else {
          // Normal thread mode - show all threads
          return threads.map((thread, index) => {
            const html = extractImagesAndRenderMarkdown(thread);
            const showAvatar = index > 0; // Only show avatar for threads after the first one
            const shouldApplyDropCap = currentPage === 0 && index === 0;
            const finalHtml = shouldApplyDropCap ? applyDropCapToFirstWord(html) : html;
            return `<div class="thread-post" data-thread-index="${index}">
              ${showAvatar ? `<div class="thread-avatar">
                <img src="/assets/Logo.PNG" alt="Story Avatar" class="thread-avatar-img">
              </div>` : ''}
              <div class="thread-content">
                ${showAvatar ? `<div class="thread-header">
                  <span class="thread-handle">@sunnysideup_storys</span>
                  <span class="thread-count">${index + 1}/${threads.length}</span>
                </div>` : ''}
                <div class="thread-text">${finalHtml}</div>
              </div>
            </div>`;
          }).join('');
        }
      }
      
      // Handle all pages uniformly with distracted mode support
      if (isDistractedMode) {
        const allElements = pages[currentPage].split('\n\n');
        const revealedCount = allElements.length - remainingElements.length;
        const visibleElements = allElements.slice(0, Math.max(1, revealedCount));
        
        let content = visibleElements.map((element, index) => {
          const html = extractImagesAndRenderMarkdown(element);
          // Apply drop cap only to first paragraph of first page
          const shouldApplyDropCap = currentPage === 0 && index === 0;
          return `<div class="content-element">${shouldApplyDropCap ? applyDropCapToFirstWord(html) : html}</div>`;
        }).join('');
        
        if (remainingElements.length > 0) {
          content += '<div class="tap-to-continue">Tap to Continue</div>';
        }
        
        return content;
      } else {
        // Normal mode - show full page content
        const html = extractImagesAndRenderMarkdown(pages[currentPage]);
        return currentPage === 0 ? applyDropCapToFirstWord(html) : html;
      }
    } catch (error) {
      console.error('Error rendering page:', error);
      return `<p>Error displaying page ${currentPage + 1}.</p>`;
    }
  };
  
  // Scroll to auth module
  const scrollToAuthModule = () => {
    if (authModuleRef.current) {
      authModuleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Toggle images visibility
  const toggleImagesVisibility = () => {
    setImagesHidden(!imagesHidden);
    // Add or remove a class to the container to control image visibility via CSS
    if (!imagesHidden) {
      document.documentElement.classList.add('hide-images');
    } else {
      document.documentElement.classList.remove('hide-images');
    }
  };
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    // Add or remove a class to the container to control dark mode via CSS
    if (!isDarkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  };

  // Toggle thread mode
  const toggleThreadMode = () => {
    setIsThreadMode(!isThreadMode);
  };

  // Group paragraphs into thread-like chunks
  const groupIntoThreads = (markdown) => {
    const paragraphs = markdown.split('\n\n').filter(p => p.trim() !== '');
    const threads = [];
    let currentThread = [];
    let currentCharCount = 0;
    const maxCharsPerThread = 280; // Twitter's character limit
    
    paragraphs.forEach(paragraph => {
      const charCount = paragraph.length;
      
      // If adding this paragraph would exceed the limit, start a new thread
      // BUT only if we already have content in current thread (don't break paragraphs)
      if (currentCharCount + charCount > maxCharsPerThread && currentThread.length > 0) {
        threads.push(currentThread.join('\n\n'));
        currentThread = [paragraph];
        currentCharCount = charCount;
      } else {
        currentThread.push(paragraph);
        currentCharCount += charCount + 2; // +2 for the \n\n separator
      }
    });
    
    // Add the last thread if it has content
    if (currentThread.length > 0) {
      threads.push(currentThread.join('\n\n'));
    }
    
    return threads;
  };
  

  useEffect(() => {
    // Set initial font size and line height
    const mainContent = document.getElementById('story-content');
    if (mainContent) {
      mainContent.style.fontSize = `${fontSize}px`;
      mainContent.style.lineHeight = `${lineHeight}px`;
      
      // Apply to paragraphs as well
      const paragraphs = mainContent.querySelectorAll('p');
      paragraphs.forEach(p => {
        p.style.fontSize = `${fontSize}px`;
        p.style.lineHeight = `${lineHeight}px`;
      });
      
      // Apply to thread mode text specifically
      const threadTexts = mainContent.querySelectorAll('.thread-text, .thread-text p');
      threadTexts.forEach(p => {
        p.style.fontSize = `${fontSize}px`;
        p.style.lineHeight = `${lineHeight}px`;
      });
    }
  }, [fontSize, lineHeight]);


  // Show header when page changes
  useEffect(() => {
    setHeaderVisible(true);
    // Reset scroll position tracking
    lastScrollTop.current = 0;
  }, [currentPage]);

  
  return (
    <div className={`container ${rainActive ? 'rain-active' : ''} ${windActive ? 'wind-active' : ''} ${imagesHidden ? 'images-hidden' : ''} ${(isDarkMode || isThreadMode) ? 'dark-mode' : ''} ${isThreadMode ? 'thread-mode' : ''}`}>
      <div className="rain-overlay"></div>
      <div className="wind-overlay"></div>
      
      {headerVisible && <Header onLoginClick={scrollToAuthModule} />}
      
      <Toolbar 
        isVisible={toolbarVisible}
        rainActive={rainActive}
        windActive={windActive}
        bgmActive={bgmActive}
        isDistractedMode={isDistractedMode}
        imagesHidden={imagesHidden}
        isDarkMode={isDarkMode}
        isThreadMode={isThreadMode}
        onRainToggle={toggleRain}
        onWindToggle={toggleWind}
        onBgmToggle={toggleBgm}
        onDistractedToggle={toggleDistractedMode}
        onImagesToggle={toggleImagesVisibility}
        onDarkModeToggle={toggleDarkMode}
        onThreadModeToggle={toggleThreadMode}
        onIncreaseFontSize={increaseFontSize}
        onDecreaseFontSize={decreaseFontSize}
        onRainVolumeChange={handleRainVolumeChange}
        onWindVolumeChange={handleWindVolumeChange}
        onBgmVolumeChange={handleBgmVolumeChange}
      />
      
      <button 
        id="toggleToolbar" 
        className="toolbar-toggle" 
        aria-label="Toggle toolbar"
        onClick={toggleToolbar}
      >
        <span className="caret" style={{ transform: toolbarVisible ? 'rotate(0deg)' : 'rotate(180deg)' }}>▼</span>
        <span className="caret-text">{toolbarVisible && headerVisible ? 'Hide Toolbar & Header' : 'Show Toolbar & Header'}</span>
      </button>
      
      {currentPage === 0 && headerVisible && (
        <header>
          <h1 id="story-title">{story.title}</h1>
        </header>
      )}
      
      {currentPage > 0 && headerVisible && (
        <div className="mini-title-bar">
          <h2 className="mini-title">{story.title}</h2>
        </div>
      )}
      
      <div className="pagination">
        <div className="pagination-group">
          <button 
            id="jumpToStart" 
            onClick={jumpToStart} 
            disabled={currentPage === 0}
            className="jump-btn"
            title="Jump to first page"
          >
            ⟪
          </button>
          <button 
            id="prevPage" 
            onClick={goToPrevPage} 
            disabled={currentPage === 0}
          >
            Previous
          </button>
        </div>
        
        <span id="pageIndicator">Page {currentPage + 1} of {pages.length}</span>
        
        <div className="pagination-group">
          <button 
            id="nextPage" 
            onClick={goToNextPage} 
            disabled={currentPage === pages.length - 1 || (!user && getTotalPagesRead() >= 10)}
          >
            Next
          </button>
          <button 
            id="jumpToEnd" 
            onClick={jumpToEnd} 
            disabled={currentPage === pages.length - 1 || (!user && getTotalPagesRead() >= 10)}
            className="jump-btn"
            title="Jump to last page"
          >
            ⟫
          </button>
        </div>
      </div>
      
      <main 
        id="story-content" 
        className={isThreadMode ? 'thread-mode' : ''}
        onClick={isDistractedMode ? revealNextElement : undefined}
        ref={mainContentRef}
      >
        <div 
          className={`content-wrapper ${isThreadMode ? 'thread-wrapper' : ''}`}
          dangerouslySetInnerHTML={{ __html: getPageContent() }}
        />
      </main>
      
      {/* Onboarding popup */}
      <OnboardingPopup />
      
      {!user && !showAuth && getTotalPagesRead() >= 10 && (
        <div ref={authModuleRef}>
          <AuthModule />
        </div>
      )}
      
      {showAuth && (
        <div className="auth-popup-overlay" onClick={() => setShowAuth(false)}>
          <div className="auth-popup" onClick={e => e.stopPropagation()}>
            <AuthModule />
            <button className="close-auth" onClick={() => setShowAuth(false)}>×</button>
          </div>
        </div>
      )}
      
      <footer>
        <p>© 2025 <a href="https://instagram.com/sunnysideup_storys" target="_blank" rel="noopener noreferrer">Sunny Sideup Storys LLC</a>. All rights reserved.</p>
      </footer>
      
      <audio id="rainSound" ref={rainSoundRef} loop>
        <source src="/assets/calming-rain-low.mp3" type="audio/mpeg" />
      </audio>
      
      <audio id="bgmSound" ref={bgmSoundRef} loop>
        <source src="/assets/ambient-mystery.mp3" type="audio/mpeg" />
      </audio>
      
      <audio id="windSound" ref={windSoundRef} loop>
        <source src="/assets/wind2.mp3" type="audio/mpeg" />
      </audio>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

export default StoryReader; 